import { useState } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { splitChange, trendTitle } from "../../lib/insights";
import EmptyState from "../EmptyState";

const fmtH = (h) => (h == null ? "—" : h < 1 ? `${Math.round(h * 60)}min` : h < 48 ? `${Math.round(h)}h` : `${(h / 24).toFixed(1)}g`);

function Trend({ pct }) {
  if (pct == null) return null;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium ${up ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(pct)}%
    </span>
  );
}

export default function StageTimes({ rows, series, companyAvg }) {
  const [openId, setOpenId] = useState(rows[0]?.stage_id);
  const pct = splitChange(series, "avg_h");
  const title = trendTitle({ subject: "Il tempo per fase", pct, neutral: "Tempo per fase", upWord: "si è allungato", downWord: "si è accorciato", stableWord: "è stabile" });
  return (
    <div className="athanor-card p-6 fade-up fade-up-3" data-testid="time-per-stage-card">
      <h2 className="text-lg font-semibold text-slate-100" data-testid="time-per-stage-title">{title}</h2>
      <p className="mb-4 text-xs text-slate-500">Ore medie che un'opportunità resta in ogni fase, confrontate con la settimana precedente.</p>
      {rows.length === 0 ? (
        <EmptyState compact testid="time-per-stage-empty" title="Nessun passaggio di fase nel periodo selezionato" ctaLabel="Sincronizza GHL" onCta={() => document.querySelector('[data-testid="sync-ghl-button"]')?.click()} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {rows.map((r) => {
              const open = openId === r.stage_id;
              return (
                <button key={r.stage_id} onClick={() => setOpenId(open ? null : r.stage_id)} data-testid={`stage-time-${r.stage_id}`}
                  className={`min-w-[150px] flex-1 rounded-lg border p-3 text-left transition-colors ${open ? "border-[#C5A059]/40 bg-[#C5A059]/5" : "border-zinc-800/70 hover:border-zinc-700"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-slate-500">{r.stage}</span>
                    <Trend pct={r.trend_pct} />
                  </div>
                  <div className="mt-1 tabular-nums text-xl font-bold text-slate-50">{fmtH(r.avg_h)}</div>
                  <div className="text-xs text-slate-500">media azienda: {fmtH(companyAvg)}</div>
                  {open && (
                    <div className="mt-3 space-y-1 border-t border-zinc-800/70 pt-2 text-xs text-slate-400">
                      <div className="flex justify-between"><span>Minimo</span><span className="tabular-nums text-slate-200">{fmtH(r.min_h)}</span></div>
                      <div className="flex justify-between"><span>Massimo</span><span className="tabular-nums text-slate-200">{fmtH(r.max_h)}</span></div>
                      <div className="flex justify-between"><span>Soglia</span><span className="tabular-nums text-slate-200">{fmtH(r.soglia_h)}</span></div>
                      <div className="flex justify-between"><span>Campioni</span><span className="tabular-nums text-[#C5A059]">{r.n} opp</span></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {series.length > 1 && (
            <div className="mt-5 h-44" data-testid="stage-time-chart" role="img" aria-label={title}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#64d2ff" stopOpacity={0.35} /><stop offset="100%" stopColor="#64d2ff" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="#26262a" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#8e8e93", fontSize: 12 }} tickFormatter={(d) => new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "short" })} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#8e8e93", fontSize: 12 }} tickFormatter={fmtH} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#1d1d20", border: "1px solid #26262a", borderRadius: 10, fontSize: 13 }} formatter={(v) => [fmtH(v), "media"]} labelFormatter={(d) => new Date(d).toLocaleDateString("it-IT")} />
                  <Area type="monotone" dataKey="avg_h" stroke="#e5e5ea" strokeWidth={2} fill="url(#tg)" dot={{ r: 2, fill: "#e5e5ea" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
